<template>
    <div
        class="product-shop-form"
    >
        <DebugContent>
            <details
                class="mb-6"
            >
                <summary
                    class="cursor-pointer"
                >
                    Options Debug Info
                </summary>
                <div>
                    <h5>Model</h5>
                    <ul>
                        <li
                            v-for="(v, k) in optionsModel"
                            :key="k"
                        >
                            <strong>{{ k }}:</strong>
                            {{ v }}
                        </li>
                    </ul>

                    <h5>Disabled Model</h5>
                    <ul>
                        <li
                            v-for="(opt, i) in optionsDisabledModel"
                            :key="`opt-${i}`"
                        >
                            {{ opt }}
                        </li>
                    </ul>
                </div>
            </details>

            <div class="font-size-7 font-weight-bold mb-10">
                <div>
                    Default SKU:
                    <strong class="bg-grey px-1">
                        {{ product?.defaultSkuUid }}
                    </strong>
                </div>
                <div>
                    Current SKU:
                    <strong class="px-1" :class="currentSku ? 'bg-success' : 'bg-error'">
                        {{ currentSku?.uid }}
                    </strong>
                </div>
                <div>
                    Current Saved Bundle UID:
                    <strong class="px-1">
                        {{ selectedBundleUid }}
                    </strong>
                </div>
            </div>
        </DebugContent>

        <!-- TODO: Does this need to be v-form? -->
        <v-form
            id="product-form"
            ref="elFormObj"
            @submit="onSubmit($event)"
        >

            <!-- Product Option Group -->
            <section
                v-if="product?.productOptionGroups?.length"
                class="product-option-group mb-8"
            >
                <ProductOptionGroups
                    :groups="product.productOptionGroups"
                ></ProductOptionGroups>
            </section>


            <!-- Product Options -->
            <section
                v-if="product"
                class="product-options"
            >
                <!-- Common Product Options -->
                <ProductCommonOptions
                    :options-model="optionsModel"
                    :options-list="optionsList"
                    :options-disabled-model="optionsDisabledModel"
                    @options-model="onOptionChange"
                ></ProductCommonOptions>

                <!-- Apparel Body / Size Options -->
                <template v-if="product.isGarment">
                    <DebugContent>
                        <ul class="font-size-7">
                            <li><strong>DEFAULT VALUE ITEM</strong></li>
                            <li>
                                garmentBodyUid:
                                <strong>{{ garmentBodyUid }}</strong>
                            </li>
                            <li>
                                garmentGuestBodyObj:
                                <strong>{{ garmentGuestBodyObj }}</strong>
                            </li>
                            <li>
                                garmentSizeUid:
                                <strong>{{ garmentSizeUid }}</strong>
                            </li>
                        </ul>
                    </DebugContent>
                    <client-only>
                        <v-divider
                            class="mt-4 mb-8"
                        ></v-divider>

                        <ProductGarmentOptions
                            v-model:body="garmentBodyUid"
                            v-model:guest-body-obj="garmentGuestBodyObj"
                            v-model:size="garmentSizeUid"
                            :product="product"
                        ></ProductGarmentOptions>

                        <v-divider class="mt-10 mb-6"></v-divider>
                    </client-only>
                </template>

                <!-- Bundle Templates -->
                <DebugContent>
                    <details>
                        <summary class="cursor-pointer">DEBUG: Bundle Templates</summary>
                        <ul class="font-size-7">
                            <li>
                                <div>CURRENT BUNDLE UID:</div>
                                <strong>{{ currentBundleTemplateUid }}</strong>
                            </li>
                            <li>
                                <div>MODEL:</div>
                                <strong>{{ currentBundleTemplateModel }}</strong>
                            </li>
                        </ul>
                    </details>
                </DebugContent>
                <div
                    v-if="hasBundleTemplates && bundleTemplatesStatus === 'pending'"
                    class="d-flex flex-column align-center ma-8"
                >
                    <v-progress-circular
                        width="6"
                        size="48"
                        color="accent"
                        indeterminate
                    ></v-progress-circular>
                </div>
                <template
                    v-else-if="hasBundleTemplates && currentBundleTemplateUid"
                >
                    <BundleSelector
                        :saved-bundles="savedBundles"
                        v-model:bundle-view-mode="bundleViewMode"
                        :selected-bundle="selectedBundle"
                        :current-bundle-template-uid="currentBundleTemplateUid"
                        @on-selection-change="onCustomBundleSelectionChange"
                        @on-default-reset="onResetBundleToDefaults"
                    >
                        <template
                            #templates="{ showTemplates }"
                        >
                            <BundleTemplates
                                v-show="showTemplates"
                                :key="`bundle-templates-${currentBundleTemplateUid}-${bundleTemplateResetKey}`"
                                v-model="currentBundleTemplateModel"
                                v-model:is-valid="currentBundleTemplateIsValid"
                                :sku="currentSku"
                                :current-bundle-template="currentBundleTemplateProcessed"
                                :current-bundle-template-uid="currentBundleTemplateUid"
                                @bundle-saved="onBundleSaved"
                                @update:model-value="onBundleTemplateModelUpdate"
                                @on-is-modified-change="onBundleTemplateIsModifiedChange"
                            ></BundleTemplates>
                        </template>
                    </BundleSelector>
                </template>

                <!-- Gift Card Personalization -->
                <template
                    v-if="currentSku?.isGiftCard && currentSku.isDigital"
                >
                    <article class="product-option">
                        <ProductItemPersonalizationForm
                            v-model="formPersonalization"
                            :rules="[isRequired]"
                        ></ProductItemPersonalizationForm>
                    </article>
                </template>

                <!-- Optional Services -->
                <template
                    v-if="Array.isArray(product.services) && product.services.length"
                >
                    <article
                        class="product-option"
                    >
                        <label
                            class="d-flex text-uppercase font-weight-bold"
                        >
                            Optional Services
                        </label>
                        <DebugContent>
                            <div
                                class="my-1 font-size-7"
                            >
                                Model:
                                <code>
                                    {{ optionsServicesModel }}
                                </code>
                            </div>
                        </DebugContent>
                        <ProductServices
                            v-model="optionsServicesModel"
                            :services="product.services"
                        ></ProductServices>
                    </article>
                </template>
            </section>

            <!-- Product Availability / Add-to-cart -->
            <section>
                <article>
                    <div
                        class="my-8"
                    >
                        <!-- Price Decorations -->
                        <ProductPriceDecorations
                            v-if="currentSku"
                            :current-sku="currentSku"
                            class="mb-2"
                        ></ProductPriceDecorations>

                        <v-row dense>
                            <v-col>
                                <!-- Price -->
                                <ProductPriceInfo
                                    v-if="currentSku"
                                    class="d-flex flex-column align-start justify-end"
                                    :current-sku="currentSku"
                                    :augment-prices="currentBundleTemplateModel"
                                    order-reverse
                                ></ProductPriceInfo>
                            </v-col>
                            <v-col
                                class="d-flex flex-column justify-end"
                                cols="auto"
                            >
                                <QtyInput
                                    v-model="form.qty"
                                    :disabled="!currentSku || cartStatus === 'working'"
                                    class="d-inline-block"
                                ></QtyInput>
                            </v-col>
                        </v-row>
                    </div>

                    <!-- Product Availability -->
                    <ProductAvailability
                        v-if="currentSku"
                        :current-sku="currentSku"
                        :augment-status="currentBundleTemplateModel"
                        :key="`${currentSku.uid}-${currentSku.status}`"
                        class="my-2"
                    ></ProductAvailability>

                    <!-- Submit Button -->
                    <v-btn
                        class="add-to-cart-btn"
                        type="submit"
                        :disabled="!lo_isEmpty(elFormObj?.errors) || !canSubmit"
                        :loading="cartStatus === 'working'"
                        color="primary"
                        size="x-large"
                        block
                    >
                        <template #loader>
                            <v-progress-circular indeterminate></v-progress-circular>
                        </template>
                        Add to cart
                    </v-btn>
                </article>

                <!-- Buy from retailer -->
                <ProductRetailLinks
                    v-if="canShowProductRetailLinks"
                    tag="article"
                    :retailers="product.retailers!"
                    :current-sku="currentSku!"
                    class="mt-8"
                >
                    <template #header>
                        <div
                            class="text-h6 text-center mb-1"
                        >
                            Visit Our Authorized Retailers
                        </div>
                    </template>
                </ProductRetailLinks>
            </section>

            <!-- Custom Apparel Notice -->
            <CustomApparelNotice
                v-if="product?.isGarment"
            ></CustomApparelNotice>

            <!-- Floating Submit Bar -->
            <client-only>
                <teleport
                    to=".v-main"
                    defer
                >
                    <v-slide-y-reverse-transition>
                        <ProductShopFloatingForm
                            v-if="showFloatingProductForm && currentSku"
                            :productName="product.name"
                            :current-sku="currentSku"
                            :image-stack="fullImageStack"
                            :sku-label="bundleViewMode === 'saved' ? selectedBundle?.name : null"
                        >
                            <template #submitButton>
                                <v-btn
                                    form="product-form"
                                    class="add-to-cart-btn"
                                    type="submit"
                                    :disabled="!lo_isEmpty(elFormObj?.errors) || !canSubmit"
                                    :loading="cartStatus === 'working'"
                                    color="primary"
                                    variant="flat"
                                    height="36px"
                                    aria-label="Add to Cart"
                                    block
                                >
                                    <template #loader>
                                        <v-progress-circular indeterminate></v-progress-circular>
                                    </template>
                                    Add To Cart
                                </v-btn>
                            </template>
                        </ProductShopFloatingForm>
                    </v-slide-y-reverse-transition>
                </teleport>
            </client-only>
        </v-form>
    </div>
</template>

<script lang="ts" setup>
    import * as v from 'valibot';
    import { VForm } from 'vuetify/components';
    import type { WatchStopHandle } from 'vue';
    import { isRequired } from '~/helpers/forms/validation';
    import { isError as lo_isError } from 'es-toolkit';
    import { uniqBy as lo_uniqBy } from 'es-toolkit';
    import { isEmpty as lo_isEmpty } from 'es-toolkit/compat';

    import QtyInput from '~/components/controls/QtyInput/QtyInput.vue';
    import ProductOptionGroups from '~/components/products/product/ProductOptionGroups/ProductOptionGroups.vue';
    import ProductCommonOptions from '~/components/products/product/ProductCommonOptions/ProductCommonOptions.vue';
    import ProductRetailLinks from '~/components/products/ProductRetailLinks/ProductRetailLinks.vue';
    import ProductServices from '~/components/products/ProductServices/ProductServices.vue';
    import CustomApparelNotice from '~/components/products/apparel/CustomApparelNotice/CustomApparelNotice.vue';
    import ProductPriceDecorations from '~/components/products/ProductPriceDecorations/ProductPriceDecorations.vue';
    import ProductPriceInfo from '~/components/products/ProductPriceInfo/ProductPriceInfo.vue';
    import ProductAvailability from '~/components/products/ProductAvailability/ProductAvailability.vue';
    import DebugContent from '~/components/DebugContent/DebugContent.vue';

    import type {
        ProductOptionsModel,
        AddToCartRequest,
        Product,
        BundleTemplate,
        ProductOrderOptionAttrs,
        CartLineItemPersonalization,
        CartGuestBodyResponseData,
        Sku,
        SkusMap,
        BundleTemplateModelCache,
        FullImageStack,
        ProductAttrsMaps,
        ProductAttrsGroupOption,
        ProductAttrsGroupSpec,
        ProductAttrsDisabledModel,
        BundleTemplateProcessed,
        BundleTemplateProcessedCache,
        BundleTemplateProcessedModelItem,
        Bundle
    } from '~/types';

    import {
        useCarts,
        useBundles,
        useBundleTemplates,
        useProductOptions,
        useProductAttrs,
        useTracking,
        useDebugExec,
        useProductShopFormUtils,
        useProductCustomBundleUtils,
        useDebug
    } from '~/composables';
    import { useGlobalAlert } from 'x1-base/composables';

    import {
        AddToCartModel,
        CartLineItemPersonalizationModel,
        garmentGuestBodyModel
    } from '~/models';


    const BundleSelector = defineAsyncComponent(() => import('~/components/products/product/bundle/BundleSelector/BundleSelector.vue'));
    const BundleTemplates = defineAsyncComponent(() => import('~/components/products/product/bundle-templates/BundleTemplates/BundleTemplates.vue'));
    const ProductItemPersonalizationForm = defineAsyncComponent(() => import('~/components/products/ProductItemPersonalizationForm/ProductItemPersonalizationForm.vue'));
    const ProductGarmentOptions = defineAsyncComponent(() => import('~/components/products/ProductGarmentOptions/ProductGarmentOptions.vue'));
    const ProductShopFloatingForm = defineAsyncComponent(() => import('~/components/products/ProductShopFloatingForm/ProductShopFloatingForm.vue'));

    const props = withDefaults(
        defineProps<{
            pageKey: string;
            product: Product;
            skus: Sku[];
            skusMap: SkusMap,
            attrsMaps: ProductAttrsMaps;
            optionGroups: ProductAttrsGroupOption[];
            specGroups: ProductAttrsGroupSpec[];
            preSelectedSkuUid?: Nilish<string>;
            showFloatingProductForm?: boolean;
            hasBundleTemplates?: boolean;
        }>(),
        {
            preSelectedSkuUid: null,
            showFloatingProductForm: false
        }
    );

    const emit = defineEmits<{
        'add-to-cart': [value: void];
        'sku-change': [value: Nullable<string>];
        'on-ready': [value: void];
        'on-image-stack-change': [value: Nullable<FullImageStack>];
    }>();

    let unWatchProduct: WatchStopHandle;
    let unWatchBundleTemplates: WatchStopHandle;
    let unWatchBundleViewMode: WatchStopHandle;

    const elFormObj = shallowRef<VForm>();
    const route = useRoute();
    const useCartsObj = useCarts();
    const { cartStatus } = useCartsObj;
    const currentSku = ref<Nullable<Sku>>(null);
    const nuxtApp = useNuxtApp();
    const { isLoggedIn } = nuxtApp.$user;


    const {
        getCacheUid,
        canShowProductRetailLinks
    } = useProductShopFormUtils({
        product: props.product,
        currentSku
    });
    const {
        processOptions,
        getDisabledOptionsModel,
        createAttrsCombosLookupMap,
        findMissingAttrValues,
        checkProductOrderedOptions,
        checkTopLevelChangeOption
    } = useProductOptions();
    const skusUtilsObj = useSkusUtils();
    const {
        getOrderedAttrsFromOptionsModel
    } = useProductAttrs();
    const {
        getBundleTemplates,
        processBundleTemplate,
        getImageStack
    } = useBundleTemplates();
    const {
        getBundles
    } = useBundles();
    const {
        init: initBundles,
        processBundles,
        savedBundles,
        selectedBundle,
        selectedBundleUid,
        selectBundleByUid,
        toggleQueryBundleUid,
        clearBundleSelection,
        attrsDefaultValuesModelMap,
        bundleViewMode,
        bundleTemplateIsModified,
        bundleTemplateResetKey,
        appendBundle
    } = useProductCustomBundleUtils({
        autoInit: false,
        disabled: !props.hasBundleTemplates
    });

    const getSkuByUid = skusUtilsObj.getSkuByUid.bind(0, props.skus);
    const getSkuBySelectedOrderedOptions = skusUtilsObj.getSkuBySelectedOrderedOptions.bind(0, props.skus);
    const getValuesModelBySku = skusUtilsObj.getValuesModelBySku.bind(0, props.skusMap, props.attrsMaps.attrs);
    const findBestSkusBySelectedOptions = skusUtilsObj.findBestSkusBySelectedOptions.bind(0, props.skusMap, props.attrsMaps.attrs);
    const attrTopLevelChangeOpt = ref<Nullable<string>>(checkTopLevelChangeOption(props.optionGroups));


    const prodBundleKey = `keyProdBundles_${props.pageKey}`;
    const {
        data: bundleTemplatesResponse,
        status: bundleTemplatesStatus,
        refresh: refreshBundleTemplates
    } = useLazyAsyncData<[
        Bundle[],
        Nullable<BundleTemplate[]>
    ]>(
        `${prodBundleKey}_bundleTemplates`,
        async () => {
            const b = await fetchSavedBundles();
            const bt = await fetchBundleTemplates();

            return [
                b,
                bt
            ];
        },
        {
            immediate: props.hasBundleTemplates,
            server: false
        }
    );


    const createGlobalAlert = useGlobalAlert();
    const useTrackingObj = useTracking();
    const debugConsole = useDebug();

    // Options
    const optionsInvalidValuesCombos = useState<string[][]>('keyProdFormOptInvValCombos', () => []);
    const optionsList = ref<ProductAttrsGroupOption[]>([]);
    const optionsModel = ref<ProductOptionsModel>({});
    const optionsModelOrderedAttrsVals = ref<ProductOrderOptionAttrs>(getOrderedAttrsFromOptionsModel(optionsList.value, optionsModel.value));
    const optionsDisabledModel = ref<ProductAttrsDisabledModel>({});
    const optionsServicesModel = ref<string[]>([]);

    const lastTopLevelSkuOpts: Record<string, string> = reactive({});
    const checkOrderedOptions = checkProductOrderedOptions.bind(0,
        props.attrsMaps,
        optionsModelOrderedAttrsVals,
        optionsInvalidValuesCombos,
        toggleDisabledModel
    );

    // Form
    const form: Partial<AddToCartRequest> = reactive(AddToCartModel.toPlainObject<Partial<AddToCartRequest>>());
    const formPersonalization = ref<Nullable<CartLineItemPersonalization>>(CartLineItemPersonalizationModel.toPlainObject<CartLineItemPersonalization>());

    // Garment Sizes/Bodies
    const garmentBodyUid = ref<Nullable<string>>(null);
    const garmentGuestBodyObj = ref<Nullable<CartGuestBodyResponseData>>(null);
    const garmentSizeUid = ref<Nullable<string>>(null);
    const garmentIsValid = computed(() => {
        return (props.product.isGarment ? (
            !lo_isEmpty(garmentBodyUid.value) ||
            !lo_isEmpty(garmentSizeUid.value) ||
            v.safeParse(garmentGuestBodyModel, garmentGuestBodyObj.value).success
        ) : true)
    });

    // Bundle Templates / Bundles
    const bundleTemplateProcessedCache: BundleTemplateProcessedCache = new Map();
    const bundleTemplateModelCache: BundleTemplateModelCache = new Map();
    const currentBundleTemplateProcessed = ref<Nilish<BundleTemplateProcessed>>();
    const currentBundleTemplateUid = ref<Nullable<string>>(null);
    const currentBundleTemplateModel = ref<Nullable<BundleTemplateProcessedModelItem[]>>(null);
    const currentBundleTemplateIsValid = ref(!currentBundleTemplateUid.value);

    const fullImageStack = ref<Nullable<FullImageStack>>(null);
    let reprocessBtTimer: TimeoutId = null;
    let customizeModelSnapshot: Nullable<BundleTemplateProcessedModelItem[]> = null;

    const unWatchBundleTemplateModel = watch(
        currentBundleTemplateModel,
        (newModel) => {
            debugConsole.info('Current Bundle Template Model: ', currentBundleTemplateModel);

            let imageStack: Nullable<FullImageStack> = null;

            if (newModel && currentBundleTemplateProcessed.value) {
                imageStack = getImageStack(currentBundleTemplateProcessed.value, newModel);
            }

            emit('on-image-stack-change', imageStack);
            fullImageStack.value = imageStack;
        },
        {
            immediate: true,
            deep: true
        }
    );

    async function fetchSavedBundles(): Promise<Bundle[]> {
        const uid = props.product.uid;
        const response = await getBundles({ params: { product: uid } });

        if (lo_isError(response) || !isLoggedIn.value) {
            debugConsole.info('No saved bundles found or user not logged in or error: ', response);
            return [];
        }

        debugConsole.info('Bundles Data: ', response.data);
        return response.data;
    }

    async function fetchBundleTemplates(): Promise<Nullable<BundleTemplate[]>> {
        const uid = props.product.uid;
        const response = await getBundleTemplates(uid);

        if (lo_isError(response)) return null;

        debugConsole.info('Bundles Data: ', response.data);
        return response.data;
    }

    function processCurrentBundle(uid: string): void {
        if (!bundleTemplatesResponse.value) {
            debugConsole.warn('Bundle Templates Response not ready');
            return;
        }

        const [
            bundles,
            bundleTemplates
        ] = bundleTemplatesResponse.value;

        const bundleTemplate = bundleTemplates?.find((o) => o.uid === uid);

        if (bundles?.length) {
            savedBundles.value = bundles;
            initBundles(currentSku.value?.defaultBundleUid);
            processBundles(bundleTemplate);

            if (selectedBundle.value) {
                bundleViewMode.value = 'saved';
                currentBundleTemplateIsValid.value = true;
            }
        }

        const cacheUid = getCacheUid(uid);
        const cachedBundle: Nilish<BundleTemplateProcessed> = bundleTemplateProcessedCache.get(cacheUid);
        const cashedModel: Nilish<BundleTemplateProcessedModelItem[]> = bundleTemplateModelCache.get(cacheUid);

        if (cachedBundle) {
            setCurrentBundleTemplate(uid, cachedBundle, cashedModel);
            return;
        }

        if (bundleTemplate) {
            const obj = processBundleTemplate(bundleTemplate, attrsDefaultValuesModelMap.value);
            debugConsole.info('Current Bundle Obj: ', uid, obj);
            setCurrentBundleTemplate(uid, obj, cashedModel);
        }
    }

    async function reprocessCurrentBundleTemplates(): Promise<void> {
        const uid = currentSku.value?.bundleTemplate?.uid;

        if (!uid) {
            debugConsole.warn('No current bundle template to reprocess');
            return;
        }

        const cacheUid = getCacheUid(uid);
        bundleTemplateProcessedCache.delete(cacheUid);
        bundleTemplateModelCache.delete(cacheUid);
        await nextTick();
        processCurrentBundle(uid);
    }

    function setCurrentBundleTemplate(
        uid: string,
        obj: BundleTemplateProcessed,
        model: Nilish<BundleTemplateProcessedModelItem[]>
    ): void {
        const cacheUid = getCacheUid(uid);
        bundleTemplateIsModified.value = false;
        currentBundleTemplateUid.value = uid;
        currentBundleTemplateProcessed.value = obj;
        bundleTemplateProcessedCache.set(cacheUid, obj);
        model = model || Array.from(obj.models.values()).flat();
        bundleTemplateModelCache.set(cacheUid, model);
        currentBundleTemplateModel.value = model;
    }

    async function unsetCurrentBundleTemplate(): Promise<void> {
        currentBundleTemplateUid.value = null;
        currentBundleTemplateProcessed.value = null;
        currentBundleTemplateModel.value = null;

        selectedBundle.value = null;
        selectedBundleUid.value = null;

        toggleQueryBundleUid();
    }

    function onBundleTemplateModelUpdate(model: Nullable<BundleTemplateProcessedModelItem[]>): void {
        const uid = currentBundleTemplateUid.value;
        if (model && uid) {
            const cacheUid = getCacheUid(uid);
            bundleTemplateModelCache.set(cacheUid, model);
        }
    }

    async function onBundleSaved(bundle: Bundle): Promise<void> {
        createGlobalAlert({
            type: 'success',
            text: `Bundle ${bundle.name} saved`
        });

        if (!savedBundles.value) {
            savedBundles.value = [];
        }

        await appendBundle(bundle);
        currentBundleTemplateIsValid.value = true;
        bundleViewMode.value = 'saved';

        await nextTick();
        elFormObj.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    const canSubmit = computed<boolean>(() => {
        return useCartsObj.cartStatus.value !== 'working' &&
            (currentSku.value?.canAddTo.includes('cart') ?? false) &&
            (!currentBundleTemplateUid.value ? true : currentBundleTemplateIsValid.value) &&
            garmentIsValid.value;
    });


    callOnce(() => {
        const attrsComboLookupMap = createAttrsCombosLookupMap(props.attrsMaps.attrs);
        optionsInvalidValuesCombos.value = findMissingAttrValues(
            props.skusMap,
            attrsComboLookupMap
        );
    }, { mode: 'navigation' });

    // Main
    if (props.product) {
        init();
    } else {
        unWatchProduct = watch(
            () => props.product,
            (newProd) => {
                if (newProd) {
                    unWatchProduct?.();
                    init();
                }
            },
            {
                immediate: true
            }
        );
    }

    if (import.meta.client && props.hasBundleTemplates) {
        unWatchBundleTemplates = watch(
            [
                currentSku,
                bundleTemplatesResponse
            ],
            ([
                newCurrentSku,
                newBundleTmpl
            ]) => {
                const uid = newCurrentSku?.bundleTemplate?.uid ?? null;

                if (newBundleTmpl?.[1]) {
                    if (uid) {
                        processCurrentBundle(uid);
                    } else {
                        unsetCurrentBundleTemplate();
                    }
                }
            },
            {
                immediate: true
            }
        );

        unWatchBundleViewMode = watch(
            bundleViewMode,
            (mode) => {
                if (mode === 'saved') {
                    // Snapshot the current customize model before switching
                    customizeModelSnapshot = currentBundleTemplateModel.value
                        ? [...currentBundleTemplateModel.value]
                        : null;

                    // Auto-select the previously selected bundle, or the first one
                    const bundle = selectedBundle.value || savedBundles.value?.[0] || null;
                    if (bundle) {
                        onCustomBundleSelectionChange(bundle);
                    }
                } else if (mode === 'customize') {
                    // Restore the customize model snapshot and refresh images
                    if (customizeModelSnapshot) {
                        currentBundleTemplateModel.value = customizeModelSnapshot;
                    }
                }
            }
        );
    }

    function init(): void {
        const { skus, defaultSkuUid } = props.product;
        let sku;

        // First try to get the pre-selected sku (if specified)
        if (props.preSelectedSkuUid) {
            sku = getSkuByUid(props.preSelectedSkuUid) ?? null;

            if (!sku) {
                debugConsole.warn('INIT: Pre-selected SKU not found');
            }
        } else {
            debugConsole.warn('INIT: Pre-selected SKU not set');
        }

        // Finally handle the case where there's only one sku
        if (!sku?.uid) {
            if (skus.length === 1) {
                // Last fallback, rare occasions where it's a single sku product
                debugConsole.info('INIT: Selecting first sku...');
                sku = skus[0];
            } else {
                // Last try to set sku by default
                debugConsole.warn('INIT: No sku found. Trying default: ', defaultSkuUid);
                sku = getSkuByUid(defaultSkuUid);
            }
        }

        // Final Fail/Fallback
        if (!sku?.uid) {
            debugConsole.warn('INIT: STILL no sku found; just using first one.');
            sku = skus[0];
        }

        // FAIL
        if (!sku?.uid) {
            debugConsole.warn('INIT: NO SKU. Failing.');
            return;
        }

        setCurrentSku(sku);
    }

    function setOptionsModelOrderedAttrsVals(): void {
        optionsModelOrderedAttrsVals.value = getOrderedAttrsFromOptionsModel(
            optionsList.value,
            optionsModel.value
        );
    }

    async function onOptionChange([uidAtt, uidAttVal, optionIndex]: [string, string, number]): Promise<void> {
        setOptionsModelOrderedAttrsVals();
        const isByTopLevelOpt = attrTopLevelChangeOpt.value && optionsModel.value[attrTopLevelChangeOpt.value] === uidAttVal;

        // If Top Level & Cached Sku, try...
        if (isByTopLevelOpt && !!lastTopLevelSkuOpts[uidAttVal]) {
            const skuUid = lastTopLevelSkuOpts[uidAttVal];
            debugConsole.info('TOP LEVEL CACHED SKU FOUND...: ', skuUid);
            const sku = getSkuByUid(skuUid);

            if (sku) {
                debugConsole.info('USING CACHED SKU');
                await nextTick();
                setCurrentSku(sku);
                return;
            }
        }

        // Try and find matching sku
        let sku = getSkuBySelectedOrderedOptions(optionsModelOrderedAttrsVals.value.attrsVals);
        let skuUid: Nilish<string> = sku?.uid;

        // Standard Sku Handling
        if (sku && skuUid) {
            if (attrTopLevelChangeOpt.value) {
                const key = optionsModel.value[attrTopLevelChangeOpt.value] ?? '';
                lastTopLevelSkuOpts[key] = skuUid;
            }

            await nextTick();
            setCurrentSku(sku);
            return;
        }

        debugConsole.warn('No exact match for SKU found, using fallback...');

        // Best Fallback Sku Uid
        skuUid = findBestSkusBySelectedOptions(optionsModel.value, uidAttVal);
        debugConsole.info('fallback skuUid: ', skuUid);

        // If not, fallback to default sku
        if (skuUid) {
            sku = getSkuByUid(skuUid);

            if (!sku || !skuUid) {
                debugConsole.error('FINAL TRY: No sku found');
                return;
            }

            await nextTick();
            setCurrentSku(sku);
            return;
        }

        debugConsole.warn('Option Not Available');
        setCurrentSku(null);
    }

    async function onBundleTemplateIsModifiedChange(): Promise<void> {
        bundleTemplateIsModified.value = true;
        clearBundleSelection();
    }

    async function onResetBundleToDefaults(): Promise<void> {
        const uid = currentSku.value?.bundleTemplate?.uid;
        if (!uid) return;

        const cacheUid = getCacheUid(uid);
        bundleTemplateProcessedCache.delete(cacheUid);
        bundleTemplateModelCache.delete(cacheUid);

        const [_bundles, bundleTemplates] = bundleTemplatesResponse.value || [];
        const bundle = bundleTemplates?.find((o) => o.uid === uid);
        if (!bundle) return;

        const obj = processBundleTemplate(bundle, null);
        setCurrentBundleTemplate(uid, obj, null);
        bundleTemplateResetKey.value++;
    }

    async function setCurrentSku(sku: Sku | null): Promise<void> {
        if (!sku || (sku && !sku.uid)) {
            debugConsole.info('SET SKU: ');
            currentSku.value = null;
            emit('sku-change', null);
            return;
        }

        const skuUid = sku.uid;
        currentSku.value = sku;

        optionsList.value = processOptions(props.optionGroups, sku.optionUids, props.specGroups);
        optionsModel.value = getValuesModelBySku(skuUid);
        optionsDisabledModel.value = getDisabledOptionsModel(optionsModel.value);
        setOptionsModelOrderedAttrsVals();

        if (optionsInvalidValuesCombos.value.length) {
            checkOrderedOptions();
        }

        if (import.meta.client) {
            await nextTick();
            emit('sku-change', skuUid);
            form.skuUid = skuUid;

            if (sku) {
                setSkuSlug(sku.slug);
                useTrackingObj.track('sku', sku);
                return;
            }

            // Clear sku slug
            setSkuSlug(null);
        }
    }

    function toggleDisabledModel(disable: boolean, option: string, value: string): void {
        const method = disable ? 'add' : 'delete';
        optionsDisabledModel.value[option]![method](value);
    }

    async function onCustomBundleSelectionChange(bundle: Nullable<Bundle>): Promise<void> {
        if (bundle) {
            const { uid } = bundle;
            selectBundleByUid(uid);
            await toggleQueryBundleUid(uid);
            currentBundleTemplateIsValid.value = true;
            await nextTick();
            reprocessBtTimer = setTimeout(() => {
                reprocessCurrentBundleTemplates();
            }, 300);
        } else {
            bundleViewMode.value = 'customize';
        }
    }

    async function onSubmit(e: Event): Promise<void> {
        e.preventDefault();

        const payload = AddToCartModel.toPlainObject<AddToCartRequest>(form);

        // Handle Bundle Templates
        if (bundleViewMode.value === 'saved' && selectedBundleUid.value) {
            payload.bundleUid = selectedBundleUid.value;
        } else if (currentBundleTemplateUid.value && currentBundleTemplateModel.value?.length) {
            payload.components = lo_uniqBy(currentBundleTemplateModel.value.slice(0), (o) => o.lineUid).map((c) => ({
                lineUid: c.lineUid,
                skuUid: c.lineType === 'sku' ? c.skuUid : null,
                optionUid: c.lineType === 'option' ? c.optionUid : null
            })) as any;
        }

        // Handle Garment Options
        if (garmentBodyUid.value) {
            payload.bodyResponseUid = garmentBodyUid.value;
        }
        if (garmentSizeUid.value) {
            payload.garmentSizeUid = garmentSizeUid.value;
        }
        if (garmentGuestBodyObj.value) {
            payload.bodyResponseData = garmentGuestBodyObj.value;
        }

        // Handle Services
        if (optionsServicesModel.value?.length) {
            payload.servicesUid = optionsServicesModel.value;
        }

        // Handle Personalization
        if (currentSku.value?.isGiftCard && currentSku.value?.isDigital) {
            const personalizations = CartLineItemPersonalizationModel.toPlainObject<CartLineItemPersonalization>(formPersonalization.value);

            if (personalizations) {
                Object.assign(payload, personalizations);
            }
        }

        const result = await useCartsObj.addToCart(payload);

        if (lo_isError(result)) {
            createGlobalAlert({
                text: 'Could not add item to cart. Please try again.',
                show: true,
                type: 'error'
            });
            return;
        }

        if (currentSku.value) {
            const sku = currentSku.value;

            useTrackingObj.track('add_to_cart', {
                currency: 'USD',
                value: (sku.price ?? 0) * payload.qty,
                items: [
                    {
                        item_id: payload.skuUid,
                        item_name: sku.name,
                        price: sku.price,
                        item_category: sku.marketName,
                        item_category2: sku.categoryName,
                        item_discount: sku.price - sku.userPrice,
                        quantity: payload.qty
                    }
                ]
            });
        }

        debugConsole.info('cartObj: ', useCartsObj.cartObj, ' | Results: ', result);
    }

    async function setSkuSlug(slug: Nullable<string>): Promise<void> {
        const query = Object.assign({}, route.query, { sku: slug });
        debugConsole.info('SETTING SLUG: ', slug, query);
        await navigateTo({ path: route.path, query, replace: true });
    }

    useDebugExec(() => {
        // @ts-ignore
        window.prodSkus = props.skus;
        // @ts-ignore
        window.optionsInvalidValuesCombos = optionsInvalidValuesCombos;
        // @ts-ignore
        window.optionsDisabledModel = optionsDisabledModel;
    });

    onMounted(async () => {
        await nextTick(() => {
            emit('on-ready');
        });
    });

    onBeforeUnmount(() => {
        clearTimeout(reprocessBtTimer || 0);
        unWatchProduct?.();
        unWatchBundleTemplates?.();
        unWatchBundleTemplateModel?.();
        unWatchBundleViewMode?.();
    });
</script>

<style lang="scss"></style>
